import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { storageBoxApi } from './api/storageBoxApi';
import {
  STORAGE_BOX_DEFAULT_VIEW_MODE,
  STORAGE_BOX_DEFAULT_SORT_FIELD,
  STORAGE_BOX_DEFAULT_SORT_DIR,
  STORAGE_BOX_LOCK_VAULT_IDLE_MS,
  STORAGE_BOX_SIDEBAR_WIDTH_DEFAULT,
} from './constants';
import {
  clampStorageBoxSidebarWidth,
  readStoredSidebarWidth,
  writeStoredSidebarWidth,
} from './utils/storageBoxSidebarWidth';
import {
  normalizeSortDir,
  normalizeSortField,
  parseSortOptionValue,
} from './utils/sortStorageBoxItems';
import { colorTagDisplayName, normalizeItemMeta } from './utils/itemMetaDisplay';
import StorageBoxNameModal from './components/StorageBoxNameModal';
import StorageBoxNewTextFileModal from './components/StorageBoxNewTextFileModal';
import StorageBoxConfirmDeleteModal from './components/StorageBoxConfirmDeleteModal';
import ShareModal from './components/ShareModal';
import StorageBoxLockPinModal from './components/StorageBoxLockPinModal';
import StorageBoxLockShareModal from './components/StorageBoxLockShareModal';
import StorageBoxUploadProgressBar from './components/StorageBoxUploadProgressBar';
import { clearMasterKeyBytes, encryptFile, parseMasterKeyB64 } from './utils/storageBoxLockCrypto';
import { createStorageBoxUploadJob, mergeUploadProgressPercent } from './utils/storageBoxUploadJob';

const SORT_STORAGE_KEY = 'sb-drive-sort';

const StorageBoxContext = createContext(null);

const defaultListItems = (parentId) => storageBoxApi.listItems(parentId);
const defaultUploadFiles = (parentId, files, paths, options) =>
  storageBoxApi.uploadFiles(parentId, files, paths, options);

export function useStorageBox() {
  const ctx = useContext(StorageBoxContext);
  if (!ctx) throw new Error('useStorageBox must be used within StorageBoxProvider');
  return ctx;
}

export function StorageBoxProvider({
  children,
  readOnly = false,
  publicToken = null,
  apiList = null,
  apiUpload = null,
}) {
  const [quota, setQuota] = useState(null);
  const [workspaceStats, setWorkspaceStats] = useState(null);
  const [parentId, setParentId] = useState(null);
  const [items, setItems] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState(null);
  const [navStack, setNavStack] = useState([null]);
  const [navIndex, setNavIndex] = useState(0);
  const [writable, setWritable] = useState(false);
  const [treeVersion, setTreeVersion] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [nameModal, setNameModal] = useState(null);
  const [nameModalBusy, setNameModalBusy] = useState(false);
  const [textFileModal, setTextFileModal] = useState(null);
  const [textFileModalBusy, setTextFileModalBusy] = useState(false);
  const [uploadJob, setUploadJob] = useState(null);
  const uploadCompleteTimerRef = useRef(null);
  const [deleteModalItem, setDeleteModalItem] = useState(null);
  const [deleteModalBusy, setDeleteModalBusy] = useState(false);
  const [restoreBusyId, setRestoreBusyId] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [lockShareItem, setLockShareItem] = useState(null);
  const [lockSessionToken, setLockSessionToken] = useState(null);
  const [lockRootItemId, setLockRootItemId] = useState(null);
  const [lockConfigured, setLockConfigured] = useState(false);
  const [lockVaultGateOpen, setLockVaultGateOpen] = useState(false);
  const [lockPinModal, setLockPinModal] = useState(null);
  const [activeSharedShareId, setActiveSharedShareId] = useState(null);
  const [activeSharedShare, setActiveSharedShare] = useState(null);
  const [browseMode, setBrowseMode] = useState('drive');
  const [vaultScope, setVaultScope] = useState('drive');
  const [lockMasterKeyReady, setLockMasterKeyReady] = useState(false);
  const [activeLabel, setActiveLabel] = useState(null);
  const [activeColorTag, setActiveColorTag] = useState(null);
  const [userLabels, setUserLabels] = useState([]);
  const [viewMode, setViewModeState] = useState(STORAGE_BOX_DEFAULT_VIEW_MODE);
  const [sortField, setSortFieldState] = useState(STORAGE_BOX_DEFAULT_SORT_FIELD);
  const [sortDir, setSortDirState] = useState(STORAGE_BOX_DEFAULT_SORT_DIR);
  const [sortPreferencesLoaded, setSortPreferencesLoaded] = useState(false);
  const [shellSidebarWidth, setShellSidebarWidthState] = useState(() =>
    readStoredSidebarWidth() ?? STORAGE_BOX_SIDEBAR_WIDTH_DEFAULT
  );
  const shellSidebarPersistRef = useRef(null);
  const shellSidebarWidthRef = useRef(shellSidebarWidth);
  const fileInputRef = useRef(null);
  const loadSeqRef = useRef(0);
  const treeCacheRef = useRef(new Map());
  const lockSessionTokenRef = useRef(null);
  const lockMasterKeyRef = useRef(null);
  const lockOwnerSessionTokenRef = useRef(null);
  const lockOwnerMasterKeyRef = useRef(null);
  const lockSessionIsShareScopedRef = useRef(false);
  const lockRootItemIdRef = useRef(null);
  const lockVaultGateOpenRef = useRef(false);
  const vaultScopeRef = useRef('drive');
  const activeSharedShareRef = useRef(null);
  const browseModeRef = useRef(browseMode);
  const lockIdleTimerRef = useRef(null);
  /** Bumped when the user dismisses the PIN modal or starts a new lock open flow. */
  const lockPinFlowGenRef = useRef(0);

  useEffect(() => {
    lockRootItemIdRef.current = lockRootItemId;
  }, [lockRootItemId]);

  useEffect(() => {
    browseModeRef.current = browseMode;
  }, [browseMode]);

  lockVaultGateOpenRef.current = lockVaultGateOpen;
  vaultScopeRef.current = vaultScope;

  const treeCacheKey = (folderId) =>
    folderId == null || folderId === '' ? '__root__' : String(folderId);
  const apiListRef = useRef(apiList);
  const apiUploadRef = useRef(apiUpload);
  apiListRef.current = apiList;
  apiUploadRef.current = apiUpload;

  const listFn = useCallback(
    (folderId) => (apiListRef.current ?? defaultListItems)(folderId),
    []
  );
  const uploadFn = useCallback(
    (folderId, files, paths, options) =>
      (apiUploadRef.current ?? defaultUploadFiles)(folderId, files, paths, options),
    []
  );

  const dismissUploadProgress = useCallback(() => {
    if (uploadCompleteTimerRef.current) {
      clearTimeout(uploadCompleteTimerRef.current);
      uploadCompleteTimerRef.current = null;
    }
    setUploadJob(null);
  }, []);

  useEffect(
    () => () => {
      if (uploadCompleteTimerRef.current) clearTimeout(uploadCompleteTimerRef.current);
    },
    []
  );

  const notify = useCallback((msg) => {
    setNotice({ type: 'text', message: msg });
    setTimeout(() => setNotice(null), 4000);
  }, []);

  const notifyLockVaultStatus = useCallback((variant) => {
    setNotice({ type: 'lock-vault', variant });
    setTimeout(() => setNotice(null), 4000);
  }, []);

  const clearSharedLockContext = useCallback(() => {
    setActiveSharedShareId(null);
    setActiveSharedShare(null);
    activeSharedShareRef.current = null;
  }, []);

  const applySharedLockContext = useCallback((shareRow) => {
    if (!shareRow?.shareId) {
      clearSharedLockContext();
      return;
    }
    setActiveSharedShareId(shareRow.shareId);
    setActiveSharedShare(shareRow);
    activeSharedShareRef.current = shareRow;
  }, [clearSharedLockContext]);

  const refreshLockStatus = useCallback(() => {
    if (readOnly) return;
    storageBoxApi
      .lockStatus()
      .then(async (r) => {
        setLockConfigured(Boolean(r.configured));
        if (r.lockRootItemId) {
          setLockRootItemId(r.lockRootItemId);
          lockRootItemIdRef.current = r.lockRootItemId;
        }
        const gateOpen = Boolean(r.vaultGateOpen);
        const hasLocalVaultSession = Boolean(
          lockSessionTokenRef.current && lockMasterKeyRef.current
        );
        if (gateOpen && !hasLocalVaultSession) {
          try {
            await storageBoxApi.lockVaultNow();
            setLockVaultGateOpen(false);
          } catch {
            setLockVaultGateOpen(gateOpen);
          }
          return;
        }
        setLockVaultGateOpen(gateOpen);
      })
      .catch(() => {});
  }, [readOnly]);

  const clearLockVaultSecrets = useCallback(() => {
    clearMasterKeyBytes(lockMasterKeyRef.current);
    lockMasterKeyRef.current = null;
    clearMasterKeyBytes(lockOwnerMasterKeyRef.current);
    lockOwnerMasterKeyRef.current = null;
    lockSessionTokenRef.current = null;
    lockOwnerSessionTokenRef.current = null;
    lockSessionIsShareScopedRef.current = false;
    setLockSessionToken(null);
    setLockMasterKeyReady(false);
  }, []);

  const sealLockVault = useCallback(async () => {
    const hadLocalSession = Boolean(
      lockSessionTokenRef.current || lockOwnerSessionTokenRef.current
    );
    const hadServerGate = lockVaultGateOpenRef.current;
    if (!hadLocalSession && !hadServerGate) return false;
    lockVaultGateOpenRef.current = false;
    setLockVaultGateOpen(false);
    clearLockVaultSecrets();
    clearSharedLockContext();
    setLockPinModal(null);
    try {
      await storageBoxApi.lockVaultNow();
    } catch {
      /* local session is already closed */
    }
    window.dispatchEvent(new CustomEvent('storagebox-shared-list-changed'));
    return true;
  }, [clearLockVaultSecrets, clearSharedLockContext]);

  const stashOwnerLockCredentials = useCallback(() => {
    const token = lockSessionTokenRef.current;
    const key = lockMasterKeyRef.current;
    if (!token || !key || lockSessionIsShareScopedRef.current) return;
    lockOwnerSessionTokenRef.current = token;
    if (lockOwnerMasterKeyRef.current) {
      clearMasterKeyBytes(lockOwnerMasterKeyRef.current);
    }
    lockOwnerMasterKeyRef.current = new Uint8Array(key);
  }, []);

  const restoreOwnerLockCredentials = useCallback(() => {
    const token = lockOwnerSessionTokenRef.current;
    const key = lockOwnerMasterKeyRef.current;
    if (!token || !key) return false;
    lockSessionTokenRef.current = token;
    setLockSessionToken(token);
    if (lockMasterKeyRef.current && lockMasterKeyRef.current !== key) {
      clearMasterKeyBytes(lockMasterKeyRef.current);
    }
    lockMasterKeyRef.current = key;
    lockSessionIsShareScopedRef.current = false;
    setLockMasterKeyReady(true);
    return true;
  }, []);

  const hasActiveLockVaultAccess = useCallback(() => {
    if (lockSessionTokenRef.current && lockMasterKeyRef.current) return true;
    return restoreOwnerLockCredentials();
  }, [restoreOwnerLockCredentials]);

  const lockVaultAccessActive = Boolean(lockSessionToken && lockMasterKeyReady);

  const applyOwnerLockSessionToken = useCallback((token, rootId, masterKeyB64) => {
    if (!token) return;
    lockSessionTokenRef.current = token;
    lockOwnerSessionTokenRef.current = token;
    setLockSessionToken(token);
    lockSessionIsShareScopedRef.current = false;
    if (rootId) {
      setLockRootItemId(rootId);
      lockRootItemIdRef.current = rootId;
    }
    if (masterKeyB64) {
      const parsed = parseMasterKeyB64(masterKeyB64);
      if (lockOwnerMasterKeyRef.current && lockOwnerMasterKeyRef.current !== parsed) {
        clearMasterKeyBytes(lockOwnerMasterKeyRef.current);
      }
      lockOwnerMasterKeyRef.current = new Uint8Array(parsed);
      if (lockMasterKeyRef.current && lockMasterKeyRef.current !== parsed) {
        clearMasterKeyBytes(lockMasterKeyRef.current);
      }
      lockMasterKeyRef.current = parsed;
      setLockMasterKeyReady(true);
      return;
    }
    const ownerKey = lockOwnerMasterKeyRef.current;
    if (ownerKey) {
      if (lockMasterKeyRef.current && lockMasterKeyRef.current !== ownerKey) {
        clearMasterKeyBytes(lockMasterKeyRef.current);
      }
      lockMasterKeyRef.current = ownerKey;
      setLockMasterKeyReady(true);
    }
  }, []);

  const applyLockSession = useCallback((res, { shareScoped = false } = {}) => {
    if (shareScoped) {
      stashOwnerLockCredentials();
    }
    if (res?.lockSessionToken) {
      lockSessionTokenRef.current = res.lockSessionToken;
      setLockSessionToken(res.lockSessionToken);
      if (!shareScoped) {
        lockOwnerSessionTokenRef.current = res.lockSessionToken;
        lockSessionIsShareScopedRef.current = false;
      } else {
        lockSessionIsShareScopedRef.current = true;
      }
    }
    if (res?.lockRootItemId) {
      setLockRootItemId(res.lockRootItemId);
      lockRootItemIdRef.current = res.lockRootItemId;
    }
    if (res?.masterKeyB64) {
      const parsed = parseMasterKeyB64(res.masterKeyB64);
      if (
        lockMasterKeyRef.current &&
        lockMasterKeyRef.current !== lockOwnerMasterKeyRef.current
      ) {
        clearMasterKeyBytes(lockMasterKeyRef.current);
      }
      lockMasterKeyRef.current = parsed;
      if (!shareScoped) {
        if (lockOwnerMasterKeyRef.current && lockOwnerMasterKeyRef.current !== parsed) {
          clearMasterKeyBytes(lockOwnerMasterKeyRef.current);
        }
        lockOwnerMasterKeyRef.current = new Uint8Array(parsed);
      }
    }
    setLockMasterKeyReady(Boolean(lockMasterKeyRef.current));
    if (res?.vaultGateOpen) {
      setLockVaultGateOpen(true);
      window.dispatchEvent(new CustomEvent('storagebox-shared-list-changed'));
    }
  }, [stashOwnerLockCredentials]);

  const acquireOwnerLockSessionToken = useCallback(async () => {
    if (restoreOwnerLockCredentials()) {
      return lockOwnerSessionTokenRef.current;
    }
    try {
      const res = await storageBoxApi.lockResumeOwner();
      if (res?.lockSessionToken) {
        applyLockSession(res);
        return res.lockSessionToken;
      }
    } catch {
      /* vault gate closed or lock session not on this server */
    }
    return null;
  }, [restoreOwnerLockCredentials, applyLockSession]);

  const getLockMasterKeyBytes = useCallback(() => lockMasterKeyRef.current, []);

  const resolveLockSessionToken = useCallback(
    (override) => override || lockSessionTokenRef.current || lockSessionToken,
    [lockSessionToken]
  );

  const refreshSummary = useCallback(() => {
    if (readOnly) return;
    refreshLockStatus();
    storageBoxApi
      .summary()
      .then((r) => {
        setQuota(r.quota ?? null);
        setWorkspaceStats(r.stats ?? null);
        if (r.viewMode === 'list' || r.viewMode === 'grid') {
          setViewModeState(r.viewMode);
        }
        if (r.sortField) setSortFieldState(normalizeSortField(r.sortField));
        if (r.sortDir) setSortDirState(normalizeSortDir(r.sortDir));
        if (r.sidebarWidthPx != null) {
          const w = clampStorageBoxSidebarWidth(r.sidebarWidthPx);
          setShellSidebarWidthState(w);
          writeStoredSidebarWidth(w);
        }
        setSortPreferencesLoaded(true);
      })
      .catch(() => {
        setSortPreferencesLoaded(true);
      });
  }, [readOnly, refreshLockStatus]);

  useEffect(() => {
    if (!readOnly) return;
    try {
      const raw = localStorage.getItem(SORT_STORAGE_KEY);
      if (raw) {
        const parsed = parseSortOptionValue(raw);
        setSortFieldState(parsed.sortField);
        setSortDirState(parsed.sortDir);
      }
    } catch {
      /* ignore */
    }
    setSortPreferencesLoaded(true);
  }, [readOnly]);

  const setViewMode = useCallback(
    (mode) => {
      if (readOnly) return;
      if (mode !== 'list' && mode !== 'grid') return;
      setViewModeState(mode);
      storageBoxApi.setViewMode(mode).catch(() => {
        notify('Could not save view preference');
      });
    },
    [readOnly, notify]
  );

  const setSortOrder = useCallback(
    (optionValue) => {
      const { sortField: nextField, sortDir: nextDir } = parseSortOptionValue(optionValue);
      setSortFieldState(nextField);
      setSortDirState(nextDir);
      if (readOnly) {
        try {
          localStorage.setItem(SORT_STORAGE_KEY, optionValue);
        } catch {
          /* ignore */
        }
        return;
      }
      storageBoxApi.setSortPreference(nextField, nextDir).catch(() => {
        notify('Could not save sort preference');
      });
    },
    [readOnly, notify]
  );

  const setShellSidebarWidth = useCallback((widthPx) => {
    const next = clampStorageBoxSidebarWidth(widthPx);
    shellSidebarWidthRef.current = next;
    setShellSidebarWidthState(next);
    writeStoredSidebarWidth(next);
  }, []);

  useEffect(() => {
    shellSidebarWidthRef.current = shellSidebarWidth;
  }, [shellSidebarWidth]);

  const persistShellSidebarWidth = useCallback(() => {
    if (readOnly) return;
    if (shellSidebarPersistRef.current) clearTimeout(shellSidebarPersistRef.current);
    shellSidebarPersistRef.current = setTimeout(() => {
      storageBoxApi.setSidebarWidth(shellSidebarWidthRef.current).catch(() => {
        notify('Could not save sidebar width');
      });
    }, 350);
  }, [readOnly, notify]);

  const commitShellSidebarWidth = useCallback(() => {
    if (readOnly) return;
    if (shellSidebarPersistRef.current) {
      clearTimeout(shellSidebarPersistRef.current);
      shellSidebarPersistRef.current = null;
    }
    storageBoxApi.setSidebarWidth(shellSidebarWidthRef.current).catch(() => {
      notify('Could not save sidebar width');
    });
  }, [readOnly, notify]);

  const patchTreeCacheItem = useCallback((itemId, patch) => {
    treeCacheRef.current.forEach((items, key) => {
      treeCacheRef.current.set(
        key,
        items.map((row) => (row.itemId === itemId ? normalizeItemMeta({ ...row, ...patch }) : row))
      );
    });
  }, []);

  const refreshTree = useCallback(({ keepCache = false } = {}) => {
    if (!keepCache) treeCacheRef.current.clear();
    setTreeVersion((v) => v + 1);
  }, []);

  const refreshUserLabels = useCallback(() => {
    if (readOnly) return;
    storageBoxApi
      .listLabels(vaultScope)
      .then((r) => setUserLabels((r.labels || []).map((row) => row.tagLabel).filter(Boolean)))
      .catch(() => setUserLabels([]));
  }, [readOnly, vaultScope]);

  const syncNavStackFromFolder = useCallback((folderId, crumbs) => {
    const trail = crumbs || [];
    let stack;
    if (trail.length) {
      stack = [null, ...trail.map((b) => b.itemId)];
    } else if (folderId != null && folderId !== '') {
      stack = [null, folderId];
    } else {
      stack = [null];
    }
    setNavStack(stack);
    setNavIndex(stack.length - 1);
  }, []);

  const applyOwnerLockListResult = useCallback(
    (data, effectiveParent, seq) => {
      if (seq !== loadSeqRef.current) return false;
      const crumbs = data.breadcrumbs || [];
      const lockWritable = Boolean(data.writable);
      setItems(
        (data.items || []).map((row) =>
          normalizeItemMeta({
            ...row,
            inLockVault: true,
            canManage: typeof row.canManage === 'boolean' ? row.canManage : lockWritable,
          })
        )
      );
      setBreadcrumbs(crumbs);
      setParentId(data.parentId ?? effectiveParent ?? null);
      syncNavStackFromFolder(data.parentId ?? effectiveParent ?? null, crumbs);
      setWritable(Boolean(data.writable));
      setLoadError('');
      return true;
    },
    [syncNavStackFromFolder]
  );

  const loadLockFolder = useCallback(
    async (folderId, { sharedShareId, sessionToken } = {}) => {
      const isOwnerBrowse = !sharedShareId;
      let ownerToken = sessionToken;
      if (isOwnerBrowse) {
        clearSharedLockContext();
        ownerToken = ownerToken || (await acquireOwnerLockSessionToken());
        if (!ownerToken) {
          setLoadError('');
          setLockPinModal({ mode: 'unlock' });
          return;
        }
        lockSessionTokenRef.current = ownerToken;
        setLockSessionToken(ownerToken);
        lockSessionIsShareScopedRef.current = false;
      }
      const token = resolveLockSessionToken(isOwnerBrowse ? ownerToken : sessionToken);
      if (!token) return;
      const seq = ++loadSeqRef.current;
      setLoading(true);
      setLoadError('');
      setBrowseMode(sharedShareId ? 'shared' : 'lock');
      setVaultScope('lock');
      if (sharedShareId) {
        setActiveSharedShareId(sharedShareId);
        if (activeSharedShareRef.current?.shareId === sharedShareId) {
          setActiveSharedShare(activeSharedShareRef.current);
        }
      }
      setActiveLabel(null);
      setActiveColorTag(null);
      const rootId = lockRootItemIdRef.current || lockRootItemId;
      const effectiveParent = isOwnerBrowse ? folderId ?? rootId : folderId || rootId;
      try {
        const data = await storageBoxApi.lockListItems(effectiveParent, token);
        applyOwnerLockListResult(data, effectiveParent, seq);
      } catch (e) {
        if (seq !== loadSeqRef.current) return;
        if (isOwnerBrowse) {
          clearLockVaultSecrets();
          setLoadError('');
          setLockPinModal({ mode: 'unlock' });
          setItems([]);
          setWritable(false);
          return;
        }
        const sessionExpired = e.code === 'LOCK_SESSION_EXPIRED' || e.status === 401;
        if (sessionExpired) {
          const sharedRow = activeSharedShareRef.current;
          if (sharedShareId && sharedRow?.granteeAccess && sharedRow.shareId === sharedShareId) {
            try {
              stashOwnerLockCredentials();
              const res = await storageBoxApi.lockUnlockShared(sharedRow.shareId);
              applyLockSession(res, { shareScoped: true });
              if (seq !== loadSeqRef.current) return;
              const effectiveParent = folderId || sharedRow.itemId;
              const data = await storageBoxApi.lockListItems(effectiveParent, res.lockSessionToken);
              if (seq !== loadSeqRef.current) return;
              setItems(
                (data.items || []).map((row) =>
                  normalizeItemMeta({
                    ...row,
                    inLockVault: true,
                    canManage: typeof row.canManage === 'boolean' ? row.canManage : Boolean(data.writable),
                  })
                )
              );
              setBreadcrumbs(data.breadcrumbs || []);
              setParentId(data.parentId ?? effectiveParent ?? null);
              syncNavStackFromFolder(data.parentId ?? effectiveParent ?? null, data.breadcrumbs || []);
              setWritable(Boolean(data.writable));
              setLoadError('');
              return;
            } catch {
              /* fall through to PIN modal for legacy shares */
            }
          }
          setLoadError('');
          setLockPinModal({
            mode: sharedShareId ? 'shared' : 'unlock',
            shareId: sharedShareId,
            shareRow: sharedShareId ? activeSharedShareRef.current : undefined,
          });
          setItems([]);
          setWritable(false);
          return;
        }
        setLoadError(e.message || 'load_failed');
        setItems([]);
        setWritable(false);
      } finally {
        if (seq === loadSeqRef.current) setLoading(false);
      }
    },
    [
      resolveLockSessionToken,
      lockRootItemId,
      syncNavStackFromFolder,
      clearLockVaultSecrets,
      applyLockSession,
      applyOwnerLockSessionToken,
      applyOwnerLockListResult,
      stashOwnerLockCredentials,
      acquireOwnerLockSessionToken,
      clearSharedLockContext,
    ]
  );

  const loadFolder = useCallback(
    async (folderId) => {
      const seq = ++loadSeqRef.current;
      setLoading(true);
      setLoadError('');
      setBrowseMode('drive');
      setVaultScope('drive');
      clearSharedLockContext();
      setActiveLabel(null);
      setActiveColorTag(null);
      try {
        const data = await listFn(folderId);
        if (seq !== loadSeqRef.current) return;
        const crumbs = data.breadcrumbs || [];
        setItems((data.items || []).map(normalizeItemMeta));
        setBreadcrumbs(crumbs);
        setParentId(data.parentId ?? folderId ?? null);
        syncNavStackFromFolder(folderId ?? null, crumbs);
        if (typeof data.writable === 'boolean') setWritable(data.writable);
        else setWritable(!readOnly);
      } catch (e) {
        if (seq !== loadSeqRef.current) return;
        setLoadError('load_failed');
        setItems([]);
        setWritable(false);
      } finally {
        if (seq === loadSeqRef.current) setLoading(false);
      }
    },
    [listFn, notify, readOnly, syncNavStackFromFolder, clearSharedLockContext]
  );

  const dismissLockPinModal = useCallback(() => {
    lockPinFlowGenRef.current += 1;
    setLockPinModal(null);
    const hasAccess = Boolean(lockSessionTokenRef.current && lockMasterKeyRef.current);
    if (!hasAccess && vaultScopeRef.current === 'lock') {
      setVaultScope('drive');
      setBrowseMode('drive');
      clearSharedLockContext();
      loadFolder(null);
    }
  }, [loadFolder, clearSharedLockContext]);

  const loadFolderRef = useRef(loadFolder);
  loadFolderRef.current = loadFolder;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      refreshSummary();
      await loadFolderRef.current(null);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
      loadSeqRef.current += 1;
    };
  }, [refreshSummary]);

  useEffect(() => {
    refreshUserLabels();
  }, [refreshUserLabels]);

  const navigateTo = useCallback(
    async (folderId, selectAfter = null) => {
      const targetId = folderId ?? null;
      if (targetId === parentId) {
        if (selectAfter) setSelected(selectAfter);
        return;
      }
      if (!selectAfter) setSelected(null);
      const lockRootId = lockRootItemIdRef.current || lockRootItemId;
      const useSharedLockSession =
        Boolean(activeSharedShareId) &&
        browseMode === 'shared' &&
        lockRootId &&
        targetId !== lockRootId;
      const inLockBrowsing = browseMode === 'lock' || browseMode === 'shared';
      if (inLockBrowsing) {
        if (useSharedLockSession) {
          await loadLockFolder(targetId, { sharedShareId: activeSharedShareId });
        } else {
          await loadLockFolder(targetId ?? lockRootId);
        }
      } else if (vaultScope === 'lock' && resolveLockSessionToken()) {
        if (useSharedLockSession) {
          await loadLockFolder(targetId, { sharedShareId: activeSharedShareId });
        } else {
          await loadLockFolder(targetId ?? lockRootId);
        }
      } else {
        await loadFolder(targetId);
      }
      if (selectAfter) setSelected(selectAfter);
    },
    [
      loadFolder,
      loadLockFolder,
      parentId,
      browseMode,
      activeSharedShareId,
      activeSharedShare,
      vaultScope,
      resolveLockSessionToken,
      lockRootItemId,
    ]
  );

  const goToRoot = useCallback(async () => {
    setSelected(null);
    setSearchQuery('');
    setVaultScope('drive');
    const lockPromise = sealLockVault();
    const loadPromise = loadFolder(null);
    const [locked] = await Promise.all([lockPromise, loadPromise]);
    if (locked) notifyLockVaultStatus('locked');
  }, [loadFolder, sealLockVault, notifyLockVaultStatus]);

  const openLockFolder = useCallback(async () => {
    if (readOnly) return;
    const flowId = ++lockPinFlowGenRef.current;
    const flowAborted = () => lockPinFlowGenRef.current !== flowId;

    setSelected(null);
    setSearchQuery('');
    setLoadError('');
    clearSharedLockContext();
    setVaultScope('lock');

    const sessionReady = hasActiveLockVaultAccess();
    // Show PIN modal on the same tap (before await) so mobile browsers allow focusing the input.
    if (lockConfigured && !sessionReady) {
      setLoading(false);
      if (!flowAborted()) setLockPinModal({ mode: 'unlock' });
    } else {
      setLoading(true);
    }

    try {
      const status = await storageBoxApi.lockStatus();
      if (flowAborted()) return;
      setLockConfigured(Boolean(status.configured));
      if (status.lockRootItemId) {
        setLockRootItemId(status.lockRootItemId);
        lockRootItemIdRef.current = status.lockRootItemId;
      }
      if (!status.configured) {
        setLoading(false);
        if (!flowAborted()) setLockPinModal({ mode: 'setup' });
        return;
      }
      const rootId = status.lockRootItemId || lockRootItemIdRef.current || lockRootItemId;
      if (!rootId) {
        setLoading(false);
        notify('Lock folder root not found');
        return;
      }
      const ownerToken = await acquireOwnerLockSessionToken();
      if (flowAborted()) return;
      if (!ownerToken) {
        setLoading(false);
        if (!flowAborted()) setLockPinModal({ mode: 'unlock' });
        return;
      }
      setLockPinModal(null);
      await loadLockFolder(rootId, { sessionToken: ownerToken });
    } catch (e) {
      if (flowAborted()) return;
      setLoading(false);
      notify(e.message || 'Could not open Lock folder');
    }
  }, [
    readOnly,
    lockRootItemId,
    loadLockFolder,
    notify,
    acquireOwnerLockSessionToken,
    lockConfigured,
    hasActiveLockVaultAccess,
    clearSharedLockContext,
  ]);

  const openLockPinSettings = useCallback(() => {
    if (readOnly) return;
    setLockPinModal({ mode: 'change' });
  }, [readOnly]);

  const openSharedItem = useCallback(
    async (shareRow) => {
      if (!shareRow?.itemId) return;
      setSelected(null);
      setSearchQuery('');
      if (shareRow.requiresLockPin) {
        setVaultScope('lock');
        applySharedLockContext(shareRow);
        if (shareRow.granteeAccess) {
          if (!lockVaultAccessActive && !hasActiveLockVaultAccess()) {
            notify('Unlock your Lock folder first to open secure shares');
            setLockPinModal({ mode: 'unlock' });
            return;
          }
          try {
            stashOwnerLockCredentials();
            const res = await storageBoxApi.lockUnlockShared(shareRow.shareId);
            applyLockSession(res, { shareScoped: true });
            await loadLockFolder(shareRow.itemId || res.itemId, {
              sharedShareId: shareRow.shareId,
              sessionToken: res?.lockSessionToken,
            });
            if (shareRow.type === 'file') {
              setSelected({
                itemId: shareRow.itemId,
                name: shareRow.name,
                type: 'file',
                inLockVault: true,
                canManage: false,
              });
            }
          } catch (e) {
            if (e.code === 'LOCK_VAULT_LOCKED') {
              notify('Unlock your Lock folder first to open secure shares');
              setLockPinModal({ mode: 'unlock' });
            } else {
              notify(e.message || 'Could not open secure share');
            }
          }
          return;
        }
        setLockPinModal({ mode: 'shared', shareId: shareRow.shareId, shareRow });
        return;
      }
      setVaultScope('drive');
      applySharedLockContext(shareRow);
      await loadFolder(shareRow.itemId);
    },
    [
      loadFolder,
      loadLockFolder,
      applyLockSession,
      notify,
      lockVaultAccessActive,
      hasActiveLockVaultAccess,
      stashOwnerLockCredentials,
      applySharedLockContext,
    ]
  );

  const handleLockPinSuccess = useCallback(
    async (res) => {
      if (res?.switchMode === 'reset') {
        setLockPinModal({ mode: 'reset' });
        return;
      }
      if (lockPinModal?.mode === 'change') {
        if (res?.lockSessionToken) {
          applyLockSession(res, { shareScoped: false });
        }
        setLockPinModal(null);
        return;
      }
      const shared = lockPinModal?.shareRow;
      const shareScoped = Boolean(shared?.shareId || lockPinModal?.shareId);
      if (shareScoped) {
        stashOwnerLockCredentials();
        if (shared) applySharedLockContext(shared);
      }
      applyLockSession(res, { shareScoped });
      setLockPinModal(null);
      if (lockPinModal?.mode === 'unlock') {
        notifyLockVaultStatus('unlocked');
      }
      const token = res?.lockSessionToken;
      const rootId = res.lockRootItemId || lockRootItemId;
      if (shared?.shareId || res?.itemId) {
        await loadLockFolder(shared?.itemId || res.itemId, {
          sharedShareId: shared?.shareId || lockPinModal?.shareId,
          sessionToken: token,
        });
      } else {
        await loadLockFolder(rootId, { sessionToken: token });
      }
    },
    [
      applyLockSession,
      loadLockFolder,
      lockRootItemId,
      lockPinModal,
      stashOwnerLockCredentials,
      applySharedLockContext,
      notifyLockVaultStatus,
    ]
  );

  const openBin = useCallback(async () => {
    if (readOnly) return;
    const scope = vaultScope;
    if (scope === 'drive') clearLockVaultSecrets();
    const seq = ++loadSeqRef.current;
    setSelected(null);
    setSearchQuery('');
    setLoading(true);
    setLoadError('');
    setBrowseMode('bin');
    setActiveLabel(null);
    setActiveColorTag(null);
    try {
      const data = await storageBoxApi.listBin(scope);
      if (seq !== loadSeqRef.current) return;
      setItems(
        (data.items || []).map((row) =>
          normalizeItemMeta(scope === 'lock' ? { ...row, inLockVault: true } : row)
        )
      );
      setBreadcrumbs([]);
      setParentId(null);
      setNavStack([null]);
      setNavIndex(0);
      setWritable(scope === 'drive' || Boolean(resolveLockSessionToken()));
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      const msg = e.message || 'Failed to load Bin';
      setLoadError(msg);
      notify(msg);
      setItems([]);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [readOnly, notify, clearLockVaultSecrets, vaultScope, resolveLockSessionToken]);

  const openFavorites = useCallback(async () => {
    if (readOnly) return;
    const scope = vaultScope;
    if (scope === 'drive') clearLockVaultSecrets();
    const seq = ++loadSeqRef.current;
    setSelected(null);
    setSearchQuery('');
    setLoading(true);
    setLoadError('');
    setBrowseMode('favorites');
    setActiveLabel(null);
    setActiveColorTag(null);
    try {
      const data = await storageBoxApi.listFavorites(scope);
      if (seq !== loadSeqRef.current) return;
      setItems(
        (data.items || []).map((row) =>
          normalizeItemMeta(scope === 'lock' ? { ...row, inLockVault: true } : row)
        )
      );
      setBreadcrumbs([]);
      setParentId(null);
      setNavStack([null]);
      setNavIndex(0);
      setWritable(scope === 'drive' || Boolean(resolveLockSessionToken()));
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      const msg = e.message || 'Failed to load Favorites';
      setLoadError(msg);
      notify(msg);
      setItems([]);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [readOnly, notify, clearLockVaultSecrets, vaultScope, resolveLockSessionToken]);

  const openLabelView = useCallback(
    async (tagLabel) => {
      if (readOnly) return;
      const label = String(tagLabel || '').trim();
      if (!label) return;
      const seq = ++loadSeqRef.current;
      setSelected(null);
      setSearchQuery('');
      setLoading(true);
      setLoadError('');
      setBrowseMode('label');
      setActiveLabel(label);
      setActiveColorTag(null);
      try {
        const data = await storageBoxApi.listItemsByLabel(label, vaultScope);
        if (seq !== loadSeqRef.current) return;
        setItems(
          (data.items || []).map((row) =>
            normalizeItemMeta(vaultScope === 'lock' ? { ...row, inLockVault: true } : row)
          )
        );
        setBreadcrumbs([]);
        setParentId(null);
        setNavStack([null]);
        setNavIndex(0);
        setWritable(vaultScope === 'drive' || Boolean(resolveLockSessionToken()));
      } catch (e) {
        if (seq !== loadSeqRef.current) return;
        const msg = e.message || 'Failed to load label';
        setLoadError(msg);
        notify(msg);
        setItems([]);
      } finally {
        if (seq === loadSeqRef.current) setLoading(false);
      }
    },
    [readOnly, notify, vaultScope, resolveLockSessionToken]
  );

  const openColorTagView = useCallback(
    async (colorTag) => {
      if (readOnly) return;
      const tag = String(colorTag || '').trim().toLowerCase();
      if (!tag) return;
      const seq = ++loadSeqRef.current;
      const tagName = colorTagDisplayName(tag) || tag;
      setSelected(null);
      setSearchQuery('');
      setLoadError('');
      try {
        const data = await storageBoxApi.listItemsByColorTag(tag, vaultScope);
        if (seq !== loadSeqRef.current) return;
        const nextItems = (data.items || []).map((row) =>
          normalizeItemMeta(vaultScope === 'lock' ? { ...row, inLockVault: true } : row)
        );
        if (!nextItems.length) {
          notify(`No items tagged ${tagName}`);
          return;
        }
        setBrowseMode('colorTag');
        setActiveLabel(null);
        setActiveColorTag(tag);
        setItems(nextItems);
        setBreadcrumbs([]);
        setParentId(null);
        setNavStack([null]);
        setNavIndex(0);
        setWritable(vaultScope === 'drive' || Boolean(resolveLockSessionToken()));
      } catch {
        if (seq !== loadSeqRef.current) return;
        notify(`No items tagged ${tagName}`);
      }
    },
    [readOnly, notify, vaultScope, resolveLockSessionToken]
  );

  const lockFolderNow = useCallback(async () => {
    const leaveLock = vaultScopeRef.current === 'lock';
    const locked = await sealLockVault();
    if (!locked) return;
    notifyLockVaultStatus('locked');
    if (leaveLock) {
      setVaultScope('drive');
      setBrowseMode('drive');
      await loadFolder(null);
    }
  }, [sealLockVault, notifyLockVaultStatus, loadFolder]);

  const scheduleLockVaultIdleSeal = useCallback(() => {
    if (readOnly) return;
    if (lockIdleTimerRef.current) {
      window.clearTimeout(lockIdleTimerRef.current);
      lockIdleTimerRef.current = null;
    }
    if (!lockSessionTokenRef.current || !lockMasterKeyRef.current) return;
    lockIdleTimerRef.current = window.setTimeout(async () => {
      lockIdleTimerRef.current = null;
      if (!lockSessionTokenRef.current || !lockMasterKeyRef.current) return;
      const mode = browseModeRef.current;
      const locked = await sealLockVault();
      if (!locked) return;
      if (mode === 'lock' || mode === 'shared') {
        setVaultScope('drive');
        setBrowseMode('drive');
        await loadFolder(null);
      }
      notifyLockVaultStatus('locked-idle');
    }, STORAGE_BOX_LOCK_VAULT_IDLE_MS);
  }, [readOnly, sealLockVault, loadFolder, notifyLockVaultStatus]);

  useEffect(() => {
    if (readOnly || !lockVaultAccessActive) {
      if (lockIdleTimerRef.current) {
        window.clearTimeout(lockIdleTimerRef.current);
        lockIdleTimerRef.current = null;
      }
      return undefined;
    }
    const bumpIdleTimer = () => scheduleLockVaultIdleSeal();
    const passive = { passive: true };
    window.addEventListener('pointerdown', bumpIdleTimer, passive);
    window.addEventListener('keydown', bumpIdleTimer, passive);
    window.addEventListener('touchstart', bumpIdleTimer, passive);
    scheduleLockVaultIdleSeal();
    return () => {
      window.removeEventListener('pointerdown', bumpIdleTimer, passive);
      window.removeEventListener('keydown', bumpIdleTimer, passive);
      window.removeEventListener('touchstart', bumpIdleTimer, passive);
      if (lockIdleTimerRef.current) {
        window.clearTimeout(lockIdleTimerRef.current);
        lockIdleTimerRef.current = null;
      }
    };
  }, [readOnly, lockVaultAccessActive, scheduleLockVaultIdleSeal]);

  const reloadCurrentView = useCallback(() => {
    if (browseMode === 'lock') return loadLockFolder(parentId);
    if (browseMode === 'shared' && activeSharedShareId) {
      return loadLockFolder(parentId, { sharedShareId: activeSharedShareId });
    }
    if (browseMode === 'bin') return openBin();
    if (browseMode === 'favorites') return openFavorites();
    if (browseMode === 'label' && activeLabel) return openLabelView(activeLabel);
    if (browseMode === 'colorTag' && activeColorTag) return openColorTagView(activeColorTag);
    return loadFolder(parentId);
  }, [
    browseMode,
    activeSharedShareId,
    activeLabel,
    activeColorTag,
    loadLockFolder,
    openBin,
    openFavorites,
    openLabelView,
    openColorTagView,
    loadFolder,
    parentId,
  ]);

  const emptyBin = useCallback(async () => {
    if (readOnly) return;
    try {
      await storageBoxApi.emptyBin(vaultScope);
      refreshSummary();
      refreshTree();
      await openBin();
      notify('Bin emptied');
    } catch (e) {
      notify(e.message || 'Could not empty Bin');
    }
  }, [readOnly, notify, refreshSummary, refreshTree, openBin, vaultScope]);

  const updateItemMeta = useCallback(
    async (item, patch) => {
      if (!item?.itemId || readOnly) return;
      const rollback = normalizeItemMeta({ ...item });
      const optimistic = normalizeItemMeta({ ...item, ...patch });

      const applyLocal = (meta) => {
        setItems((prev) =>
          prev.map((row) => (row.itemId === item.itemId ? { ...row, ...meta } : row))
        );
        if (selected?.itemId === item.itemId) {
          setSelected((prev) => (prev ? { ...prev, ...meta } : prev));
        }
        patchTreeCacheItem(item.itemId, meta);
      };

      applyLocal(optimistic);

      try {
        const res = await storageBoxApi.setItemMeta(item.itemId, patch);
        const merged = normalizeItemMeta({ ...item, ...(res.item || patch) });
        applyLocal(merged);
        if (browseMode === 'favorites' && patch.isFavorite === false) {
          setItems((prev) => prev.filter((row) => row.itemId !== item.itemId));
        }
        refreshTree({ keepCache: true });
        refreshUserLabels();
        if (browseMode === 'label' && activeLabel && patch.tagLabel !== undefined) {
          await openLabelView(activeLabel);
        }
        if (browseMode === 'colorTag' && activeColorTag && patch.colorTag !== undefined) {
          await openColorTagView(activeColorTag);
        }
      } catch (e) {
        applyLocal(rollback);
        notify(e.message || 'Could not update item');
      }
    },
    [
      readOnly,
      selected,
      browseMode,
      activeLabel,
      activeColorTag,
      notify,
      refreshTree,
      patchTreeCacheItem,
      refreshUserLabels,
      openLabelView,
      openColorTagView,
    ]
  );

  const goBack = useCallback(() => {
    if (navIndex <= 0) return;
    setSelected(null);
    const targetId = navStack[navIndex - 1];
    const lockRootId = lockRootItemIdRef.current || lockRootItemId;
    const useSharedLockSession =
      Boolean(activeSharedShareId) &&
      browseMode === 'shared' &&
      lockRootId &&
      targetId !== lockRootId;
    const inLockBrowsing = browseMode === 'lock' || browseMode === 'shared';
    if (inLockBrowsing) {
      if (useSharedLockSession) {
        loadLockFolder(targetId, { sharedShareId: activeSharedShareId });
      } else {
        loadLockFolder(targetId ?? lockRootId);
      }
      return;
    }
    loadFolder(targetId);
  }, [
    loadFolder,
    loadLockFolder,
    navIndex,
    navStack,
    browseMode,
    activeSharedShareId,
    lockRootItemId,
  ]);

  const goForward = useCallback(() => {
    if (navIndex >= navStack.length - 1) return;
    setSelected(null);
    const targetId = navStack[navIndex + 1];
    const lockRootId = lockRootItemIdRef.current || lockRootItemId;
    const useSharedLockSession =
      Boolean(activeSharedShareId) &&
      browseMode === 'shared' &&
      lockRootId &&
      targetId !== lockRootId;
    const inLockBrowsing = browseMode === 'lock' || browseMode === 'shared';
    if (inLockBrowsing) {
      if (useSharedLockSession) {
        loadLockFolder(targetId, { sharedShareId: activeSharedShareId });
      } else {
        loadLockFolder(targetId ?? lockRootId);
      }
      return;
    }
    loadFolder(targetId);
  }, [
    loadFolder,
    loadLockFolder,
    navIndex,
    navStack,
    browseMode,
    activeSharedShareId,
    lockRootItemId,
  ]);

  const loadChildrenForTree = useCallback(
    async (folderId) => {
      if (apiListRef.current) {
        const data = await listFn(folderId);
        return (data.items || []).map(normalizeItemMeta);
      }
      const key = treeCacheKey(folderId);
      const cached = treeCacheRef.current.get(key);
      if (cached) return cached;
      const data = await storageBoxApi.listTreeChildren(folderId ?? null);
      const items = (data.items || []).map(normalizeItemMeta);
      treeCacheRef.current.set(key, items);
      return items;
    },
    [listFn]
  );

  const searchActive = !readOnly && searchQuery.trim().length >= 2;

  useEffect(() => {
    if (!searchActive) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    const term = searchQuery.trim();
    const handle = setTimeout(() => {
      const token = resolveLockSessionToken();
      if (vaultScope === 'lock' && !token) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      const searchPromise =
        vaultScope === 'lock' && token
          ? storageBoxApi.lockSearchItems(term, token)
          : storageBoxApi.searchItems(term, undefined, 'drive');
      searchPromise
        .then((r) =>
          setSearchResults(
            (r.items || []).map((row) =>
              normalizeItemMeta(vaultScope === 'lock' ? { ...row, inLockVault: true } : row)
            )
          )
        )
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 280);
    return () => clearTimeout(handle);
  }, [searchQuery, searchActive, vaultScope, resolveLockSessionToken]);

  const clearSearch = useCallback(() => setSearchQuery(''), []);

  const openItem = useCallback(
    (item) => {
      const fromSearch = searchActive;
      if (fromSearch) clearSearch();
      if (item.type === 'folder') {
        navigateTo(item.itemId, item);
        return;
      }
      if (fromSearch || item.parentId !== parentId) {
        navigateTo(item.parentId || null, item);
      } else {
        setSelected(item);
      }
    },
    [searchActive, clearSearch, navigateTo, parentId]
  );

  const handleUpload = useCallback(
    async (fileList, relativePaths, uploadParentId) => {
      if (readOnly || !writable) return;
      const files = [...fileList];
      if (!files.length) return;
      const destinationId = uploadParentId !== undefined ? uploadParentId : parentId;
      if (uploadCompleteTimerRef.current) {
        clearTimeout(uploadCompleteTimerRef.current);
        uploadCompleteTimerRef.current = null;
      }
      const totalPlainBytes = files.reduce((sum, file) => sum + (file.size || 0), 0);
      setUploadJob(createStorageBoxUploadJob(files));

      const patchJob = (patch) => {
        setUploadJob((prev) => (prev ? { ...prev, ...patch } : prev));
      };

      try {
        if ((browseMode === 'lock' || browseMode === 'shared') && resolveLockSessionToken()) {
          const token = resolveLockSessionToken();
          const masterKey = lockMasterKeyRef.current;
          if (!masterKey) {
            throw new Error('Lock session expired. Unlock again.');
          }
          const encMeta = [];
          const cipherFiles = [];
          const encryptWeight = 40;
          let encryptedBytes = 0;
          for (let i = 0; i < files.length; i += 1) {
            const file = files[i];
            patchJob({
              phase: 'encrypting',
              fileIndex: i + 1,
              currentName: file.name,
            });
            const enc = await encryptFile(file, masterKey);
            encMeta.push({
              name: enc.name,
              encIv: enc.encIv,
              encAuthTag: enc.encAuthTag,
              encFormat: enc.encFormat,
              encChunkPlainSize: enc.encChunkPlainSize,
              sizeBytes: enc.sizeBytes,
              mimeType: enc.mimeType,
            });
            cipherFiles.push(
              new File([enc.ciphertext], `${enc.name}.enc`, { type: 'application/octet-stream' })
            );
            encryptedBytes += file.size || 0;
            patchJob({
              loadedBytes: encryptedBytes,
              totalBytes: totalPlainBytes,
              percent: totalPlainBytes
                ? Math.min(
                    encryptWeight,
                    Math.round((encryptedBytes / totalPlainBytes) * encryptWeight)
                  )
                : Math.min(encryptWeight, Math.round(((i + 1) / files.length) * encryptWeight)),
            });
          }
          const cipherBytes = cipherFiles.reduce((sum, file) => sum + (file.size || 0), 0);
          patchJob({
            phase: 'uploading',
            totalBytes: totalPlainBytes + cipherBytes,
            loadedBytes: encryptedBytes,
          });
          await storageBoxApi.uploadLockFiles(destinationId, cipherFiles, token, {
            encMeta,
            onProgress: (evt) => {
              patchJob({
                phase: 'uploading',
                loadedBytes: encryptedBytes + (evt.loaded || 0),
                totalBytes: totalPlainBytes + (evt.total || cipherBytes),
                percent: mergeUploadProgressPercent(evt, encryptWeight),
              });
            },
          });
        } else {
          patchJob({ phase: 'uploading', totalBytes: totalPlainBytes });
          await uploadFn(destinationId, files, relativePaths, {
            onProgress: (evt) => {
              patchJob({
                phase: 'uploading',
                loadedBytes: evt.loaded || 0,
                totalBytes: evt.total || totalPlainBytes,
                percent:
                  evt.percent != null ? evt.percent : mergeUploadProgressPercent(evt, 0),
              });
            },
          });
        }
        patchJob({
          phase: 'complete',
          percent: 100,
          loadedBytes: totalPlainBytes,
          totalBytes: totalPlainBytes,
        });
        notify('Upload complete');
        refreshSummary();
        refreshTree();
        if (browseMode === 'lock' || browseMode === 'shared') {
          await loadLockFolder(
            destinationId,
            browseMode === 'shared' ? { sharedShareId: activeSharedShareId } : {}
          );
        } else {
          await loadFolder(destinationId);
        }
        uploadCompleteTimerRef.current = setTimeout(() => {
          setUploadJob(null);
          uploadCompleteTimerRef.current = null;
        }, 4500);
      } catch (e) {
        const message = e.message || 'Upload failed';
        patchJob({
          phase: 'error',
          errorMessage: message,
        });
        notify(message);
      }
    },
    [
      readOnly,
      writable,
      uploadFn,
      parentId,
      browseMode,
      resolveLockSessionToken,
      activeSharedShareId,
      notify,
      refreshSummary,
      refreshTree,
      patchTreeCacheItem,
      loadFolder,
      loadLockFolder,
    ]
  );

  const openCreateFolderModal = useCallback(
    (createParentId) => {
      if (!writable || readOnly) return;
      const targetParent =
        typeof createParentId === 'string' || createParentId === null
          ? createParentId
          : parentId;
      setNameModal({
        mode: 'createFolder',
        createParentId: targetParent,
      });
    },
    [writable, readOnly, parentId]
  );

  const openNewTextFileModal = useCallback(
    (kind, createParentId) => {
      if (!writable || readOnly) return;
      if (kind !== 'plain' && kind !== 'rich') return;
      const targetParent =
        typeof createParentId === 'string' || createParentId === null
          ? createParentId
          : parentId;
      setTextFileModal({
        kind,
        createParentId: targetParent,
        editorKey: Date.now(),
      });
    },
    [writable, readOnly, parentId]
  );

  const closeNewTextFileModal = useCallback(() => {
    if (textFileModalBusy) return;
    setTextFileModal(null);
  }, [textFileModalBusy]);

  const saveNewTextFile = useCallback(
    async (file, _normalizedName, options) => {
      if (!textFileModal || textFileModalBusy) return;
      setTextFileModalBusy(true);
      try {
        await handleUpload([file], null, textFileModal.createParentId);
        if (!options?.saveAs) {
          setTextFileModal(null);
        }
      } finally {
        setTextFileModalBusy(false);
      }
    },
    [textFileModal, textFileModalBusy, handleUpload]
  );

  const canManageItem = useCallback(
    (item) => {
      if (!item || readOnly) return false;
      if (browseMode === 'bin') return writable;
      if (item.canManage) return true;
      return writable && (browseMode === 'lock' || browseMode === 'shared');
    },
    [readOnly, writable, browseMode]
  );

  const openRenameModal = useCallback(
    (item) => {
      if (!canManageItem(item)) return;
      setNameModal({ mode: 'rename', item });
    },
    [canManageItem]
  );

  const renameItem = useCallback(
    async (item, name) => {
      if (!canManageItem(item)) return;
      const trimmed = (name || '').trim();
      if (!trimmed) return;
      await storageBoxApi.renameItem(item.itemId, trimmed);
      patchTreeCacheItem(item.itemId, { name: trimmed });
      if (selected?.itemId === item.itemId) {
        setSelected({ ...item, name: trimmed });
      }
      notify('Renamed');
      refreshTree({ keepCache: true });
      if (browseMode === 'lock' || browseMode === 'shared') {
        await loadLockFolder(
          parentId,
          browseMode === 'shared' ? { sharedShareId: activeSharedShareId } : {}
        );
      } else {
        await loadFolder(parentId);
      }
    },
    [
      canManageItem,
      selected,
      notify,
      refreshTree,
      patchTreeCacheItem,
      browseMode,
      parentId,
      activeSharedShareId,
      loadLockFolder,
      loadFolder,
    ]
  );

  const closeNameModal = useCallback(() => {
    if (nameModalBusy) return;
    setNameModal(null);
  }, [nameModalBusy]);

  const submitNameModal = useCallback(
    async (name) => {
      if (!nameModal || nameModalBusy) return;
      setNameModalBusy(true);
      try {
        if (nameModal.mode === 'createFolder') {
          const rawParent = nameModal.createParentId;
          const targetParent =
            typeof rawParent === 'string' || rawParent === null ? rawParent : parentId;
          const token = resolveLockSessionToken();
          if ((browseMode === 'lock' || browseMode === 'shared') && token) {
            await storageBoxApi.lockCreateFolder(targetParent, name, token);
          } else {
            await storageBoxApi.createFolder(targetParent, name);
          }
          treeCacheRef.current.delete(treeCacheKey(targetParent));
          notify('Folder created');
          refreshTree();
          if (browseMode === 'lock' || browseMode === 'shared') {
            await loadLockFolder(
              parentId,
              browseMode === 'shared' ? { sharedShareId: activeSharedShareId } : {}
            );
          } else {
            await loadFolder(parentId);
          }
        } else if (nameModal.mode === 'rename' && nameModal.item) {
          await renameItem(nameModal.item, name);
        }
        setNameModal(null);
      } catch (e) {
        notify(e.message || 'Failed');
      } finally {
        setNameModalBusy(false);
      }
    },
    [
      nameModal,
      nameModalBusy,
      parentId,
      browseMode,
      resolveLockSessionToken,
      activeSharedShareId,
      notify,
      refreshTree,
      renameItem,
      loadFolder,
      loadLockFolder,
      selected,
    ]
  );

  const openDeleteModal = useCallback(
    (item) => {
      if (!canManageItem(item)) return;
      setDeleteModalItem(item);
    },
    [canManageItem]
  );

  const closeDeleteModal = useCallback(() => {
    if (deleteModalBusy) return;
    setDeleteModalItem(null);
  }, [deleteModalBusy]);

  const confirmDelete = useCallback(async () => {
    if (!deleteModalItem || deleteModalBusy) return;
    setDeleteModalBusy(true);
    const deleted = deleteModalItem;
    try {
      await storageBoxApi.deleteItem(deleted.itemId);
      if (selected?.itemId === deleted.itemId) setSelected(null);
      setDeleteModalItem(null);
      refreshSummary();
      refreshTree();

      const deletedFolderId = deleted.type === 'folder' ? deleted.itemId : null;
      const viewingInsideDeletedFolder =
        deletedFolderId &&
        (parentId === deletedFolderId || breadcrumbs.some((b) => b.itemId === deletedFolderId));

      if (viewingInsideDeletedFolder) {
        await loadFolder(deleted.parentId ?? null);
      } else {
        await reloadCurrentView();
      }

      notify('Moved to Bin');
    } catch (e) {
      notify(e.message || 'Delete failed');
    } finally {
      setDeleteModalBusy(false);
    }
  }, [
    deleteModalItem,
    deleteModalBusy,
    selected,
    parentId,
    breadcrumbs,
    refreshSummary,
    refreshTree,
    reloadCurrentView,
    loadFolder,
    notify,
  ]);

  const restoreBinItem = useCallback(
    async (item) => {
      if (!item?.itemId || readOnly || !writable || browseMode !== 'bin') return;
      if (item.daysUntilPurge != null && item.daysUntilPurge <= 0) {
        notify('This item expired and can no longer be restored');
        return;
      }
      setRestoreBusyId(item.itemId);
      try {
        await storageBoxApi.restoreItem(item.itemId);
        if (selected?.itemId === item.itemId) setSelected(null);
        refreshSummary();
        refreshTree();
        await openBin();
        notify(`Restored “${item.name}”`);
      } catch (e) {
        notify(e.message || 'Restore failed');
      } finally {
        setRestoreBusyId(null);
      }
    },
    [readOnly, writable, browseMode, selected, refreshSummary, refreshTree, openBin, notify]
  );

  const openShareModal = useCallback(
    (item) => {
      if (!item || readOnly) return;
      if (browseMode === 'lock' || browseMode === 'shared' || item.inLockVault) {
        setLockShareItem(item);
        return;
      }
      setShareItem(item);
    },
    [readOnly, browseMode]
  );

  const closeShareModal = useCallback(() => setShareItem(null), []);

  const nameModalProps = useMemo(() => {
    if (!nameModal) return null;
    if (nameModal.mode === 'createFolder') {
      return {
        title: 'New folder',
        label: 'Folder name',
        initialName: '',
        confirmLabel: 'Create',
      };
    }
    return {
      title: nameModal.item?.type === 'folder' ? 'Rename folder' : 'Rename file',
      label: 'Name',
      initialName: nameModal.item?.name || '',
      confirmLabel: 'Save',
    };
  }, [nameModal]);

  const value = useMemo(
    () => ({
      readOnly,
      publicToken,
      quota,
      workspaceStats,
      parentId,
      items,
      breadcrumbs,
      selected,
      setSelected,
      loading,
      loadError,
      notice,
      writable,
      treeVersion,
      fileInputRef,
      navIndex,
      navStackLength: navStack.length,
      navigateTo,
      goToRoot,
      goBack,
      goForward,
      loadChildrenForTree,
      handleUpload,
      uploadJob,
      dismissUploadProgress,
      openCreateFolderModal,
      openNewTextFileModal,
      openRenameModal,
      renameItem,
      openDeleteModal,
      restoreBinItem,
      restoreBusyId,
      openShareModal,
      loadFolder,
      refreshTree,
      patchTreeCacheItem,
      refreshSummary,
      retryLoad: () => reloadCurrentView(),
      notify,
      searchQuery,
      setSearchQuery,
      searchResults,
      searchLoading,
      searchActive,
      clearSearch,
      openItem,
      browseMode,
      openBin,
      openFavorites,
      openLabelView,
      openColorTagView,
      activeLabel,
      activeColorTag,
      userLabels,
      refreshUserLabels,
      emptyBin,
      updateItemMeta,
      reloadCurrentView,
      viewMode,
      setViewMode,
      sortField,
      sortDir,
      sortPreferencesLoaded,
      setSortOrder,
      shellSidebarWidth,
      setShellSidebarWidth,
      persistShellSidebarWidth,
      commitShellSidebarWidth,
      lockSessionToken,
      lockRootItemId,
      lockConfigured,
      lockVaultGateOpen,
      lockVaultAccessActive,
      openLockFolder,
      openLockPinSettings,
      lockFolderNow,
      openSharedItem,
      activeSharedShareId,
      activeSharedShare,
      vaultScope,
      lockMasterKeyReady,
      inLockVault: vaultScope === 'lock',
      getLockMasterKeyBytes,
      clearLockVaultSecrets,
    }),
    [
      readOnly,
      publicToken,
      quota,
      workspaceStats,
      parentId,
      items,
      breadcrumbs,
      selected,
      loading,
      loadError,
      notice,
      writable,
      treeVersion,
      navIndex,
      navStack.length,
      navigateTo,
      goToRoot,
      goBack,
      goForward,
      loadChildrenForTree,
      handleUpload,
      uploadJob,
      dismissUploadProgress,
      openCreateFolderModal,
      openNewTextFileModal,
      openRenameModal,
      renameItem,
      openDeleteModal,
      restoreBinItem,
      restoreBusyId,
      openShareModal,
      loadFolder,
      refreshTree,
      patchTreeCacheItem,
      refreshSummary,
      notify,
      searchQuery,
      searchResults,
      searchLoading,
      searchActive,
      clearSearch,
      openItem,
      browseMode,
      openBin,
      openFavorites,
      openLabelView,
      openColorTagView,
      activeLabel,
      activeColorTag,
      userLabels,
      refreshUserLabels,
      emptyBin,
      updateItemMeta,
      reloadCurrentView,
      viewMode,
      setViewMode,
      sortField,
      sortDir,
      sortPreferencesLoaded,
      setSortOrder,
      shellSidebarWidth,
      setShellSidebarWidth,
      persistShellSidebarWidth,
      commitShellSidebarWidth,
      lockSessionToken,
      lockRootItemId,
      lockConfigured,
      lockVaultGateOpen,
      lockVaultAccessActive,
      openLockFolder,
      openLockPinSettings,
      lockFolderNow,
      openSharedItem,
      activeSharedShareId,
      activeSharedShare,
      vaultScope,
      lockMasterKeyReady,
      browseMode,
      getLockMasterKeyBytes,
      clearLockVaultSecrets,
      restoreBinItem,
      restoreBusyId,
    ]
  );

  return (
    <StorageBoxContext.Provider value={value}>
      {children}
      <StorageBoxUploadProgressBar job={uploadJob} onDismiss={dismissUploadProgress} />
      {nameModalProps ? (
        <StorageBoxNameModal
          isOpen={Boolean(nameModal)}
          onClose={closeNameModal}
          busy={nameModalBusy}
          onSubmit={submitNameModal}
          {...nameModalProps}
        />
      ) : null}
      <StorageBoxNewTextFileModal
        isOpen={Boolean(textFileModal)}
        kind={textFileModal?.kind || 'plain'}
        editorKey={textFileModal?.editorKey || 0}
        busy={textFileModalBusy}
        onClose={closeNewTextFileModal}
        onSave={saveNewTextFile}
      />
      <StorageBoxConfirmDeleteModal
        isOpen={Boolean(deleteModalItem)}
        item={deleteModalItem}
        busy={deleteModalBusy}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
      />
      {shareItem ? <ShareModal item={shareItem} onClose={closeShareModal} onNotify={notify} /> : null}
      {lockShareItem ? (
        <StorageBoxLockShareModal
          item={lockShareItem}
          lockSessionToken={lockSessionToken}
          onClose={() => setLockShareItem(null)}
          onNotify={notify}
        />
      ) : null}
      {lockPinModal?.mode ? (
        <StorageBoxLockPinModal
          mode={lockPinModal.mode}
          shareId={lockPinModal.shareId}
          onClose={dismissLockPinModal}
          onSuccess={handleLockPinSuccess}
          onSwitchMode={(nextMode) => setLockPinModal((prev) => ({ ...prev, mode: nextMode }))}
          onNotify={notify}
        />
      ) : null}
    </StorageBoxContext.Provider>
  );
}
